import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentComplaintAPI } from '../services/api';
import './Auth.css';

const TrackComplaint = () => {
  const [trackingData, setTrackingData] = useState({
    ticket_id: '',
    phone_number: ''
  });
  const [complaint, setComplaint] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setTrackingData({
      ...trackingData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await studentComplaintAPI.trackComplaint(trackingData);
      setComplaint(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Complaint not found';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return '#3b82f6'; // blue
      case 'in_progress':
        return '#f59e0b'; // amber
      case 'resolved':
        return '#10b981'; // green
      case 'closed':
        return '#6b7280'; // gray
      default:
        return '#3b82f6';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open':
        return 'Open';
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (complaint) {
    return (
      <div className="auth-container">
        <div className="auth-backdrop"></div>
        <div className="auth-card fade-in" style={{ maxWidth: '700px' }}>
          <div className="auth-header">
            <h1 className="auth-title">Complaint Details</h1>
            <p className="auth-subtitle">Ticket ID: {complaint.ticket_id}</p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{
              background: getStatusColor(complaint.status) + '20',
              border: `2px solid ${getStatusColor(complaint.status)}`,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Current Status
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: getStatusColor(complaint.status)
              }}>
                {getStatusLabel(complaint.status)}
              </div>
            </div>

            <div className="info-message">
              <p><strong>Student Name:</strong> {complaint.student_name}</p>
              <p><strong>Complaint Type:</strong> {complaint.complaint_type || 'N/A'}</p>
              <p><strong>Submitted:</strong> {formatDate(complaint.created_at)}</p>
              <p><strong>Last Updated:</strong> {formatDate(complaint.updated_at)}</p>
            </div>

            <div style={{ marginTop: '16px' }}>
              <h3 style={{ marginBottom: '8px', fontSize: '16px' }}>Complaint Description:</h3>
              <div style={{
                background: 'var(--bg-secondary)',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                {complaint.description}
              </div>
            </div>

            {complaint.status_history && complaint.status_history.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Status History:</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {complaint.status_history.map((history, index) => (
                    <div
                      key={index}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '12px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        borderLeft: `3px solid ${getStatusColor(history.new_status)}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>
                          {history.old_status && (
                            <span style={{ color: 'var(--text-secondary)' }}>
                              {getStatusLabel(history.old_status)} →{' '}
                            </span>
                          )}
                          <strong>{getStatusLabel(history.new_status)}</strong>
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {formatDate(history.changed_at)}
                        </span>
                      </div>
                      {history.notes && (
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          Note: {history.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
            <button
              onClick={() => {
                setComplaint(null);
                setTrackingData({ ticket_id: '', phone_number: '' });
              }}
              className="btn btn-primary btn-block"
            >
              Track Another Complaint
            </button>
            <button
              onClick={() => navigate('/student-complaint')}
              className="btn btn-secondary btn-block"
            >
              Submit New Complaint
            </button>
            <button
              onClick={() => navigate('/login')}
              className="auth-link"
              style={{ marginTop: '8px' }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-backdrop"></div>
      <div className="auth-card fade-in">
        <div className="auth-header">
          <h1 className="auth-title">Track Your Complaint</h1>
          <p className="auth-subtitle">Enter your ticket ID and phone number</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleTrack} className="auth-form">
          <div className="form-group">
            <label className="form-label">Ticket ID *</label>
            <input
              type="text"
              name="ticket_id"
              className="form-input"
              value={trackingData.ticket_id}
              onChange={handleChange}
              required
              autoFocus
              placeholder="TKT-000001"
              style={{ textTransform: 'uppercase' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Phone Number * (10 digits)</label>
            <input
              type="tel"
              name="phone_number"
              className="form-input"
              value={trackingData.phone_number}
              onChange={handleChange}
              required
              pattern="[0-9]{10}"
              maxLength="10"
              placeholder="9876543210"
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
              Enter the phone number you used when submitting the complaint
            </small>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Track Complaint'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have a ticket yet?{' '}
            <button onClick={() => navigate('/student-complaint')} className="auth-link">
              Submit Complaint
            </button>
          </p>
          <p>
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="auth-link">
              Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrackComplaint;
